'use client';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import s from './Bookmark.module.scss';
import Icon from '../Icon';
import { t } from '../../i18n';
import classNames from 'classnames';
import { useBookmarkContext } from '../../utils/bookmark-provider';

const toRelativePath = (url) => {
    if (!url || !url.startsWith('http')) return url;
    try {
        const u = new URL(url);
        return u.pathname + u.search;
    } catch {
        return url;
    }
};

const Bookmark = ({
    url: rawUrl,
    pageId,
    isSmall = false,
    isFullWidth,
    modifiers = [],
    categories = [],
    alternativeTitle,
    noAnimation = false,
}) => {
    const url = toRelativePath(rawUrl);
    const { bookmarkedPages, addBookmark, removeBookmark } =
        useBookmarkContext();
    const [status, setStatus] = useState('idle');

    const isBookmarked = bookmarkedPages.some(
        (b) => toRelativePath(b.url) === url
    );

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    };

    const handleClick = async () => {
        const newBookmarkedState = !isBookmarked;
        setStatus(newBookmarkedState ? 'added' : 'removed');

        try {
            const csrfToken = getCookie('csrftoken');

            const res = await fetch('/wt/bookmark/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    url,
                    page_id: pageId,
                    categories,
                    alternativeTitle,
                }),
            });

            if (!res.ok) {
                const errorData = await res.json();
                console.error('Failed to update bookmark:', errorData);
            } else {
                if (newBookmarkedState) {
                    addBookmark({ url, title: alternativeTitle, categories });
                } else {
                    removeBookmark(url);
                }
            }
        } catch (err) {
            console.error('Bookmark request failed:', err);
        }

        setTimeout(() => setStatus('idle'), 1500);
    };

    const getLabel = () => {
        if (status === 'added') return t('general.bookmarkAdded');
        if (status === 'removed') return t('general.bookmarkRemoved');
        return isBookmarked
            ? t('general.bookmarkRemove')
            : t('general.bookmarkPage');
    };

    const getIconName = () => {
        if (!noAnimation) {
            if (status === 'added') return 'CheckGreenCircle';
            if (status === 'removed') return 'BookmarkRemoving';
        }
        return isBookmarked ? 'BookmarkMinus' : 'Bookmark';
    };

    const classes = classNames(
        s.Root,
        {
            [s['Root--Added']]: status === 'added',
            [s['Root--Removed']]: status === 'removed',
            [s['Root--Small']]: isSmall,
            [s['Root--FullWidth']]: isFullWidth,
        },
        modifiers.map((modifier) => s[`Root--${modifier}`])
    );

    return (
        <button
            className={classes}
            onClick={handleClick}
            disabled={status !== 'idle'}>
            <Icon name={getIconName()} className={s.Icon} />
            {!isSmall && <span className={s.Text}>{getLabel()}</span>}
        </button>
    );
};

Bookmark.propTypes = {
    url: PropTypes.string.isRequired,
    pageId: PropTypes.number,
    isSmall: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    modifiers: PropTypes.array,
    categories: PropTypes.array,
    alternativeTitle: PropTypes.string,
};

export default Bookmark;
